import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Modal from 'bootstrap/js/dist/modal';

export interface IVideo {
	src: string;
}

export interface IVideoProps extends IBaseElementProps {
	url?: IVideo;
	/**
	 * You can paste embed code from video streaming sites like YouTube or Vimeo here.
	 * @control textarea
	 */
	oembed?: string;
	poster?: IImage;
	playLabel?: string;
	autoPlay?: boolean;
}

export const getYoutubeIDFromURL = (url: string): string | boolean => {
	var regExp =
		/^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
	var match = url.match(regExp);
	return match && match[7].length == 11 ? match[7] : false;
};

/**
 * Video
 * @block
 * @requiresHooks
 */
const Video: React.FC<IVideoProps> = ({
	className,
	classNames = [],
	url,
	oembed,
	poster,
	playLabel = 'Play video',
	autoPlay = false,
}) => {
	const [isPlaying, setIsPlaying] = useState(autoPlay);
	const [iframeLoaded, setIframeLoaded] = useState(false);
	const ref = useRef(null);
	let embedURL = '';
	let isYoutubeVideo = false;
	let thumbURL = poster ? poster.src : '';

	const handleClick = () => {
		if (isYoutubeVideo && ref.current) {
			const modal = new Modal(ref.current);
			modal.show();
		} else {
			ref.current && (ref.current! as HTMLVideoElement).play();
		}
		if (!iframeLoaded) {
			const videoEmbed = ref.current.querySelector(
				'.video__embed-content',
			);
			videoEmbed.innerHTML = oembed;
			const videoEmbedIframe = videoEmbed.querySelector('iframe');
			videoEmbedIframe.addEventListener('load', () => {
				setIframeLoaded(true);
			});
		}
	};

	useEffect(() => {
		if (!ref || !ref.current) {
			return;
		}

		if (!!url) {
			const video = ref.current! as HTMLVideoElement;
			video.addEventListener('play', () => {
				setIsPlaying(true);
			});

			video.addEventListener('pause', () => {
				setIsPlaying(false);
			});
		} else if (!!oembed && ref.current) {
			document.body.appendChild(ref.current);
		}

		const videoModal = ref.current as HTMLDivElement;

		if (videoModal) {
			videoModal.addEventListener('hide.bs.modal', function (event) {
				const videoIframe = videoModal.querySelector('iframe');
				const videoIframeSrc = videoModal.querySelector('iframe').src;
				videoIframe.src = videoIframeSrc;
			});
		}
	}, []);

	if (!!oembed) {
		const urlMatches = oembed!.match(/\bhttps?:\/\/\S+/gi);
		if (urlMatches && urlMatches.length > 0) {
			embedURL = urlMatches[0];

			if (
				embedURL.indexOf('youtube.com/') > -1 ||
				embedURL.indexOf('youtu.be/') > -1
			) {
				isYoutubeVideo = true;

				if (!thumbURL) {
					thumbURL = `https://i1.ytimg.com/vi/${getYoutubeIDFromURL(
						embedURL,
					)}/hqdefault.jpg`;
				}
			}
		}
	}

	return (
		<div
			className={
				className ||
				cn(
					'video',
					isPlaying && 'video--playing',
					thumbURL && 'video--poster',
					...classNames,
				)
			}
			style={
				!isPlaying && thumbURL
					? { backgroundImage: `url("${thumbURL}")` }
					: {}
			}
		>
			{!!url && url.src && !oembed && (
				<video
					ref={ref}
					controls
					poster={poster?.src}
					autoPlay={autoPlay}
				>
					<source src={url.src} type="video/mp4" />
					Your browser does not support the video tag.
				</video>
			)}

			{!!oembed && (
				<div className="modal modal--video" tabIndex={-1} ref={ref}>
					<div className="modal-dialog modal-lg modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<button
									type="button"
									className="btn-close"
									data-bs-dismiss="modal"
									aria-label="Close"
								></button>
							</div>
							<div className="modal-body">
								<div ref={ref} className="video__embed">
									{!iframeLoaded && (
										<div className="video__embed-loading">
											<div
												className="spinner-border text-primary"
												role="status"
											>
												<span className="visually-hidden">
													Loading...
												</span>
											</div>
										</div>
									)}
									<div className="video__embed-content"></div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{!isPlaying && (
				<button
					className="btn-play"
					onClick={handleClick}
					aria-label="Play video"
				>
					{playLabel}
				</button>
			)}
		</div>
	);
};

export default Video;
